<template>
  <v-card class="fill-height">

    <v-card-title>
      Numbers
      
      <BeehiveIcon :link="articleLinks.numbersVoip" />

      <v-spacer></v-spacer>

      <template v-if="isVoipMigrationUser">
        <v-btn color="secondary" x-small @click="preAllocateNumberDialogOpen=true">Pre-allocate Number</v-btn>
      </template>

      <v-btn color="secondary" class="ml-2" x-small @click="reserveNumberOpen=true">Send Customer Number Reserve Mail</v-btn>

      <template v-if="getCurrentProduct.tariff_version==='TARIFF_V3'">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" color="secondary" class="ml-2" x-small @click="numberPoolOpen=true">
              <v-icon small>add</v-icon>
            </v-btn>
          </template>
          <span>Add Number</span>
        </v-tooltip>
      </template>
    </v-card-title>
    
    <v-card-text>
      <v-data-table :headers="headers" :items="getAllProductNumbers" :loading="loadingTable" disable-sort hide-default-footer class="body--text">

        <template v-slot:item.number="{ item }">
          <div class="d-flex justify-start">
            <div :class="`memNumberDot mr-2 ${item.rating}`"></div>
            {{ item.number }}
          </div>
        </template>

        <template v-slot:item.kind="{ item }">{{ getKind(item) }}</template>

        <template v-slot:item.status="{ item }">{{ getStatus(item) }}</template>

        <template v-slot:item.assignment="{ item }">{{ getAssignment(item) }}</template>

        <template v-slot:item.actions="{ item }">
          
          <div class="d-flex justify-start align-center">
            <template v-if="getPortKind(item.number_range_id)">
              <v-btn class="mr-2" color="secondary" x-small @click="editNonGeoToTranslate(item)">
                <v-icon small>edit</v-icon>
              </v-btn>
            </template>

            <v-tooltip top :disabled="disableDeleteNumberTooltip(item)">
              <template #activator="{on, attrs}">
                <div v-bind="attrs" v-on="on" class="mr-2">
                  <v-btn color="error" x-small :loading="showDeleteNumberLoader(item)" :disabled="disableDeleteNumber" @click="deleteNumberById(item)">
                    <v-icon small>delete</v-icon>
                  </v-btn>
                </div>
              </template>

              <span>This number is currently being deleted</span>
            </v-tooltip>

            <v-menu offset-x>
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="primary" x-small :disabled="disableMenu(item)" v-bind="attrs" v-on="on">
                  <v-icon>{{ mdiDotsHorizontal }}</v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item :disabled="disableMoveToAnotherProduct(item)" @click="openMoveNumberToAnotherProduct(item)">Move to another product</v-list-item>
              </v-list>
            </v-menu>
          </div>

        </template>
      </v-data-table>
    </v-card-text>
    
    <PreAllocateNumberDialog :dialogOpen="preAllocateNumberDialogOpen" @close-dialog="preAllocateNumberDialogOpen=false" />
    <ReserveNumberMailDialog :open="reserveNumberOpen" @closeDialog="reserveNumberOpen=false" />
    <AddNumbersDialog :open="numberPoolOpen" @closeDialog="numberPoolOpen=false" @numberAdded="refreshNumbers()" />
    <TranslateNonGeoNumberDialog v-model="translateNonGeoNumberOpen" @translateComplete="refreshNumbers()" />
    <MoveNumberToAnotherProduct v-model="moveNumbertoAnotherProductOpen" />
  </v-card>
</template>

<script>
  import { mapMutations, mapActions, mapGetters } from 'vuex';
  import { articleLinks } from '@/helpers/links';
  import { mdiDotsHorizontal } from '@mdi/js';
  import { isEmpty } from '@/helpers';

  import PreAllocateNumberDialog from './PreAllocateNumberDialog';
  import ReserveNumberMailDialog from './ReserveNumberMailDialog';
  import AddNumbersDialog from './AddNumbersDialog/AddNumbersDialog';
  import TranslateNonGeoNumberDialog from './TranslateNonGeoNumberDialog';
  import BeehiveIcon from '@/components/Globals/BeehiveIcon';
  import MoveNumberToAnotherProduct from './MoveNumberToAnotherProduct';

  export default {
    components: { PreAllocateNumberDialog, ReserveNumberMailDialog, AddNumbersDialog, TranslateNonGeoNumberDialog, BeehiveIcon, MoveNumberToAnotherProduct },

    props: {
      numbersLoading: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        headers: [
          { text: 'Number',  value: 'number' },
          { text: 'Kind', value: 'kind'},
          { text: 'Status', value: 'status' },
          { text: 'Assignment', value: 'assignment' },
          { text: 'Actions', value: 'actions' }
        ],

        articleLinks,

        preAllocateNumberDialogOpen: false,
        reserveNumberOpen: false,
        numberPoolOpen: false,
        translateNonGeoNumberOpen: false,
        numberDeletionPoll: null,
        numberDeleted: null,
        moveNumbertoAnotherProductOpen: false,
        isEmpty,
        mdiDotsHorizontal,      
      }
    },

    computed: {
      ...mapGetters('products', ['getAllProductNumbers', 'getFetchProductNumbersLoading', 'getCurrentProduct', 'getCurrentProductIsVoip3', 'getIsProductNumbersDeletionInProgress', 'getProductNumberDeleted']),
      ...mapGetters('numbers', ['getDeleteNumberLoading', 'getDeleteNumberError', 'getNumberRange', 'getSingleDeleteNumberLoading', 'getIsDeleteNumberPolling']),
      ...mapGetters('globals',['hasUserGotRole']),

      loadingTable() {
        return this.getFetchProductNumbersLoading || this.numbersLoading;
      },

      isVoipMigrationUser(){
        return this.hasUserGotRole('voip-migration-user');
      },

      showDeleteNumberLoader() {
        return item => this.getCurrentProductIsVoip3 && (this.getSingleDeleteNumberLoading(item.id) || !this.isEmpty(item.number_delete_init_time));
      },

      disableDeleteNumber() {
        if (this.getCurrentProductIsVoip3) {
          return this.getDeleteNumberLoading || this.getIsProductNumbersDeletionInProgress || this.getFetchProductNumbersLoading;   
        }
        return this.getDeleteNumberLoading || this.getFetchProductNumbersLoading;
      },

      disableDeleteNumberTooltip() {
        return item => !this.getCurrentProductIsVoip3 || (!this.getSingleDeleteNumberLoading(item.id) && this.isEmpty(item.number_delete_init_time));        
      },

      disableMenu() {
        return item => this.getSingleDeleteNumberLoading(item.id) || !this.isEmpty(item.number_delete_init_time);
      },

      disableMoveToAnotherProduct() {
        return item => !this.hasUserGotRole('can-move-voip-number-to-another-product') || !isEmpty(item.number_range_id);
      }
    },

    watch: {
      getIsDeleteNumberPolling: {
        handler(val) {
          if (val) {
            this.pollingNumberDeletion();
          }
        }
      }
    },

    mounted() {
      if (this.getCurrentProductIsVoip3) {
        this.UPDATE_IS_DELETE_NUMBER_POLLING(true);
      }
    },

    beforeDestroy() {
      clearTimeout(this.numberDeletionPoll);
      this.UPDATE_IS_DELETE_NUMBER_POLLING(false);
    },

    methods: {
      ...mapMutations('numberTransfers', ['SET_NON_GEO_NUMBER']),
      ...mapMutations('numbers', ['UPDATE_NUMBER_ID', 'UPDATE_IS_DELETE_NUMBER_POLLING']),
      ...mapActions('numbers', ['deleteNumber']),
      ...mapActions('products', ['fetchProductNumbers']),
      ...mapActions('alerts', ['setAlert']),

      getKind(item) {
        return item.non_geographic ? 'Non-Geo' : 'Geo';
      },

      getStatus(item) {
        if (item.non_geographic && item.status === 'Ported') {
          return 'Translated';
        }

        if (item.status === 'Porting' || item.status === 'Ported') {
          return  `${item.status} (${item.porting_status.toUpperCase()})`;
        }
        return item.status;
      },

      getAssignment(item) {
        if (item.non_geographic) {
          if (item.extension === 'Main Number') {
            return item.extension;
          }
          
          const translatedTo = this.getAllProductNumbers.find(number => number.id == item.translate_to_number_id) || {};
          return translatedTo.number || '--';
        }
        return item.extension;
      },

      getPortKind(number_range_id) {
        if (number_range_id) {
          return this.getNumberRange(number_range_id).port_kind === 'NON_GEOGRAPHIC';
        }

        return false;
      },

      async deleteNumberById(item) {
        const message = item.non_geographic ? `Are you sure you want to delete the number ${item.number}?` : 'Are you sure you wish to delete this number?';
        
        if (confirm(message)) {
          await this.deleteNumber({id: item.id, number: item.number});

          if (!this.getDeleteNumberError) {
            await this.fetchProductNumbers(this.getCurrentProduct.id);
            this.$emit('newNumberRangeAdded');

            if (this.getCurrentProductIsVoip3 && this.getIsProductNumbersDeletionInProgress) {
              this.UPDATE_IS_DELETE_NUMBER_POLLING(true);
            }                     
          }
        }
      },

      refreshNumbers(){
        this.$emit('newNumberRangeAdded');
      },
  
      editNonGeoToTranslate (item) {
        this.SET_NON_GEO_NUMBER(item);
        this.translateNonGeoNumberOpen = true;
      },

      async pollingNumberDeletion() {
        if (this.getIsProductNumbersDeletionInProgress) {
          await this.fetchProductNumbers(this.getCurrentProduct.id);
          this.$emit('newNumberRangeAdded');

          if (this.isEmpty(this.numberDeleted)) {
            this.numberDeleted = {...this.getProductNumberDeleted};
          }
          this.numberDeletionPoll = setTimeout(() => {this.pollingNumberDeletion()}, 3000);
        } else {
          if (!this.isEmpty(this.numberDeleted) && !this.getAllProductNumbers.some(number => number.id === this.numberDeleted.id)) {
            this.setAlert({type: 'success', message: `Number ${this.numberDeleted.number} deleted successfully`});
            this.numberDeleted = null;
          }
          clearTimeout(this.numberDeletionPoll);
          this.UPDATE_IS_DELETE_NUMBER_POLLING(false);
        }
      },
      
      openMoveNumberToAnotherProduct(item) {
        if (item.status === 'Assigned') {
          this.setAlert({type: 'error', message: 'Cannot move a number that is currently assigned'});
          return;
        }
        this.UPDATE_NUMBER_ID(item.id);
        this.moveNumbertoAnotherProductOpen = true;
      }
    }
  }
</script>

<style lang="scss" scoped>
  .memNumberDot {
    $radius: 16px;
    width: $radius;
    height: $radius;
    border-radius: $radius / 2;

    &.FREE { background: #CD7F32; }
    &.SILVER { background: #C4C4C4; }
    &.GOLD { background: #FFD700; }
  }
</style>